// Herkunft: https://www.tremor.so/docs/visualizations/tracker
//
// Die Balkenleiste stammt von dort. Zwei Dinge sind hier anders:
//
// 1. **Die Zustände liegen auf eigenen Token, nicht auf der Tailwind-Palette.**
//    `--color-blue-500` ist im Haus auf den Akzent umgebogen, damit kopierte
//    Bausteine die Hausfarbe treffen. Dadurch waren `blue-500` und
//    `emerald-500` derselbe Wert — „läuft" sah aus wie „erfolgreich". Die
//    `--zustand-*`-Token in `styles.css` sind genau dagegen da.
// 2. **Die Legende trägt Farbfelder.** Vorher standen dort drei nackte Wörter
//    unter einer farbigen Leiste; welches Wort zu welcher Farbe gehörte, war
//    nicht zu erkennen. Eine Legende ohne Farbe erklärt nichts.

import { Tooltip } from "@/components/Tooltip";
import { cx } from "@/lib/utils";

export interface TrackerBlock {
  key: string;
  farbe: "gruen" | "rot" | "blau" | "gelb" | "grau";
  status: string;
  tooltip: string;
  /** Steht dauerhaft im Block, erste Zeile — etwa die Nummer des Laufs. */
  titel?: string;
  /** Zweite Zeile, ruhiger — etwa die Dauer. */
  unterzeile?: string;
}

const FLAECHE: Record<TrackerBlock["farbe"], string> = {
  gruen: "bg-[var(--zustand-erfolg)]",
  rot: "bg-[var(--zustand-fehler)]",
  blau: "bg-[var(--zustand-laeuft)]",
  gelb: "bg-[var(--zustand-wartet)]",
  grau: "bg-[var(--zustand-ruht)]",
};

const SCHRIFT: Record<TrackerBlock["farbe"], string> = {
  gruen: "text-[var(--zustand-erfolg-schrift)]",
  rot: "text-[var(--zustand-fehler-schrift)]",
  blau: "text-[var(--zustand-laeuft-schrift)]",
  gelb: "text-[var(--zustand-wartet-schrift)]",
  grau: "text-[var(--zustand-ruht-schrift)]",
};

export function Tracker({
  bloecke,
  beschreibung,
}: {
  bloecke: readonly TrackerBlock[];
  beschreibung: string;
}) {
  return (
    <div>
      {/*
        Die Beschriftung steht **im** Block, nicht erst im Tooltip: Wer auf die
        Leiste sieht, will wissen, welcher Lauf das war, ohne jeden Balken
        einzeln anzufahren.

        Der Tooltip bleibt trotzdem — er trägt das Vollständige (Zustand,
        Commit), was in einem Balken nie Platz hätte. Und die Blöcke sind
        `flex-1`: Sind es viele, wird schmal, was nicht passt; deshalb
        `overflow-hidden` und eine Beschriftung, die verschwinden darf, statt
        die Leiste zu sprengen. Der `aria-label` bleibt vollständig.
      */}
      <div className="flex gap-1" aria-label={beschreibung}>
        {bloecke.map((block) => (
          <Tooltip key={block.key} content={block.tooltip} triggerAsChild>
            <button
              type="button"
              className={cx(
                "flex min-w-2 flex-1 flex-col items-center justify-center gap-0.5 overflow-hidden rounded-sm px-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500",
                block.titel ? "h-12" : "h-6",
                FLAECHE[block.farbe],
                SCHRIFT[block.farbe],
              )}
              aria-label={block.tooltip}
            >
              {block.titel ? (
                <span className="max-w-full truncate font-mono text-[11px] font-medium leading-none">
                  {block.titel}
                </span>
              ) : null}
              {block.unterzeile ? (
                <span className="max-w-full truncate font-mono text-[10px] leading-none opacity-80">
                  {block.unterzeile}
                </span>
              ) : null}
            </button>
          </Tooltip>
        ))}
      </div>
      {/*
        Je Zustand ein Eintrag mit seiner Farbe. `Map` statt `Set`, weil der
        Eintrag beides braucht — und der erste Block eines Zustands bestimmt
        die Farbe, die für alle gleichen gilt.
      */}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
        {[
          ...new Map(
            bloecke.map((block) => [block.status, block.farbe] as const),
          ).entries(),
        ].map(([status, farbe]) => (
          <li key={status} className="flex items-center gap-1.5">
            <span aria-hidden="true" className={cx("size-2.5", FLAECHE[farbe])} />
            {status}
          </li>
        ))}
      </ul>
      <p className="sr-only">{beschreibung}</p>
    </div>
  );
}
